import { useDevicesList, useUserMedia } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, nextTick, ref, shallowRef, watch } from 'vue'

interface VolumeBuffers {
  byteData: Uint8Array<ArrayBuffer>
  amplifiedData: Float32Array<ArrayBuffer>
}

// 口型同步每帧都会读取同一个 AnalyserNode，复用缓冲区可以避免持续分配数组和触发 GC。
const volumeBuffers = new WeakMap<AnalyserNode, VolumeBuffers>()

function getVolumeBuffers(analyser: AnalyserNode): VolumeBuffers {
  const size = analyser.frequencyBinCount
  const cached = volumeBuffers.get(analyser)
  if (cached && cached.byteData.length === size)
    return cached

  const buffers = {
    byteData: new Uint8Array<ArrayBuffer>(new ArrayBuffer(size)),
    amplifiedData: new Float32Array<ArrayBuffer>(new ArrayBuffer(size)),
  }
  volumeBuffers.set(analyser, buffers)
  return buffers
}

function calculateVolumeWithLinearNormalize(analyser: AnalyserNode) {
  const { byteData } = getVolumeBuffers(analyser)
  analyser.getByteFrequencyData(byteData)

  let volumeSum = 0
  for (const value of byteData)
    volumeSum += (value ** 1.2) * 1.2

  return volumeSum / byteData.length / 100
}

function calculateVolumeWithMinMaxNormalize(analyser: AnalyserNode) {
  const { byteData, amplifiedData } = getVolumeBuffers(analyser)
  analyser.getByteFrequencyData(byteData)

  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY
  for (let i = 0; i < byteData.length; i++) {
    const amplified = byteData[i] ** 1.5
    amplifiedData[i] = amplified
    min = Math.min(min, amplified)
    max = Math.max(max, amplified)
  }

  const range = max - min
  if (range === 0)
    return 0

  let volumeSum = 0
  for (const amplified of amplifiedData)
    volumeSum += (amplified - min) / range

  return volumeSum / byteData.length
}

function calculateVolume(analyser: AnalyserNode, mode: 'linear' | 'minmax' = 'linear') {
  switch (mode) {
    case 'linear':
      return calculateVolumeWithLinearNormalize(analyser)
    case 'minmax':
      return calculateVolumeWithMinMaxNormalize(analyser)
  }
}

export const useAudioContext = defineStore('audio-context', () => {
  const audioContext = shallowRef<AudioContext>(new AudioContext())

  return {
    audioContext,
    calculateVolume,
  }
})

export function useAudioDevice(requestPermission: boolean = false) {
  const devices = useDevicesList({ constraints: { audio: true }, requestPermissions: requestPermission })
  const audioInputs = computed(() => devices.audioInputs.value)
  const selectedAudioInput = ref<string>(devices.audioInputs.value.find(device => device.deviceId === 'default')?.deviceId || '')
  const deviceConstraints = computed<MediaStreamConstraints>(() => ({ audio: { deviceId: { exact: selectedAudioInput.value }, autoGainControl: true, echoCancellation: true, noiseSuppression: true } }))
  const { stream, stop: stopStream, start: startStream } = useUserMedia({ constraints: deviceConstraints, enabled: false, autoSwitch: true })

  watch(audioInputs, () => {
    if (!selectedAudioInput.value && audioInputs.value.length > 0) {
      selectedAudioInput.value = audioInputs.value.find(input => input.deviceId === 'default')?.deviceId || audioInputs.value[0].deviceId
    }
  })

  function askPermission() {
    devices.ensurePermissions()
      .then(() => nextTick())
      .then(() => {
        if (audioInputs.value.length > 0 && !selectedAudioInput.value) {
          selectedAudioInput.value = audioInputs.value.find(input => input.deviceId === 'default')?.deviceId || audioInputs.value[0].deviceId
        }
      })
      .catch((error) => {
        console.error('Error ensuring permissions:', error)
      })
  }

  // Initialize: request permission directly if needed (no onMounted dependency)
  if (requestPermission) {
    askPermission()
  }

  return {
    audioInputs,
    selectedAudioInput,
    stream,
    deviceConstraints,

    askPermission,
    startStream,
    stopStream,
  }
}

export const useSpeakingStore = defineStore('character-speaking', () => {
  const nowSpeakingAvatarBorderOpacityMin = 30
  const nowSpeakingAvatarBorderOpacityMax = 100
  const mouthOpenSize = ref(0)
  const nowSpeaking = ref(false)

  const nowSpeakingAvatarBorderOpacity = computed<number>(() => {
    if (!nowSpeaking.value)
      return nowSpeakingAvatarBorderOpacityMin

    return ((nowSpeakingAvatarBorderOpacityMin
      + (nowSpeakingAvatarBorderOpacityMax - nowSpeakingAvatarBorderOpacityMin) * mouthOpenSize.value) / 100)
  })

  return {
    mouthOpenSize,
    nowSpeaking,
    nowSpeakingAvatarBorderOpacity,
  }
})
